import React, { useCallback, useEffect, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import Underline from '@tiptap/extension-underline'
import Highlight from '@tiptap/extension-highlight'
import Image from '@tiptap/extension-image'
import TextAlign from '@tiptap/extension-text-align'
import StarterKit from "@tiptap/starter-kit";

import Modal from "react-responsive-modal";
import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchMedias } from "../api/media";
import IsEmpty from "../components/IsEmpty";

const Editor = ({getData, data, clearForm}) => {
    
    // Initialisation de l'éditeur
    const editor = useEditor({
        extensions: [StarterKit, Underline, Highlight.configure({ multicolor: true }), Image, TextAlign.configure({types: ['heading', 'paragraph'], })],
        content: "<p>UBIZNEWS</p>",
        onUpdate: ({ editor }) => {
            getData(editor.getHTML())
        },
    });

    useEffect(() => {
        if (clearForm) {
            editor.chain().focus().setContent("").run()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clearForm]);

    useEffect(() => {
        if (data) {
            editor.chain().focus().setContent(data).run();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);
    
    
    
    // Add image
    const [openModal, setOpenModal] = useState(false)

    const addImage = useCallback((image) => {
        if (image) {
            editor.chain().focus().setImage({ src: image }).run()
        }
    }, [editor])

    // Fetch All Medias
    const { 
        data: dataImage, 
        fetchNextPage, 
        hasNextPage, 
        isFetchingNextPage, 
        status,
    } = useInfiniteQuery({
        queryKey: ["medias"],
        queryFn: fetchMedias,
        getNextPageParam: (lastPage) => lastPage.nextPage,
    });



    const observer = useRef();
    const lastMediaRef = useCallback((node) => {
        if (isFetchingNextPage) return;
        if (!hasNextPage) return;

        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                fetchNextPage(); 
            }
        });

        if (node) observer.current.observe(node);
    }, [isFetchingNextPage, hasNextPage, fetchNextPage]);






    if (!editor) {
        return null;
    }

    return (
        <div className="ubiznews-editor fs-9s text-white lh-lg">
            {/* Boutons de mise en forme */}
            <div className="toolbar">
                <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} disabled={!editor.can().chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''}>
                    Bold
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} disabled={!editor.can().chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''}>
                    Italic
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} disabled={!editor.can().chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'is-active' : ''}>
                    Strike
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} disabled={!editor.can().chain().focus().toggleUnderline().run()} className={editor.isActive('underline') ? 'is-active' : ''}>
                    Underline
                </button>
                <button type="button" onClick={() => editor.chain().focus().setTextAlign('left').run()} className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}>
                    Left
                </button>
                <button type="button" onClick={() => editor.chain().focus().setTextAlign('center').run()} className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}>
                    Center
                </button>
                <button type="button" onClick={() => editor.chain().focus().setTextAlign('right').run()} className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}>
                    Right
                </button>
                <button type="button" onClick={() => editor.chain().focus().setTextAlign('justify').run()} className={editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}>
                    Justify
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleHighlight({ color: '#ea7311ab' }).run()} disabled={!editor.can().chain().focus().toggleHighlight().run()} className={editor.isActive('highlight') ? 'is-active' : ''}>
                    Highlight
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleCode().run()} disabled={!editor.can().chain().focus().toggleCode().run()} className={editor.isActive('code') ? 'is-active' : ''}>
                    Code
                </button>
                <button type="button" onClick={() => editor.chain().focus().unsetAllMarks().run()}>
                    Clear marks
                </button>
                <button type="button" onClick={() => editor.chain().focus().clearNodes().run()}>
                    Clear nodes
                </button>
                <button type="button" onClick={() => editor.chain().focus().setParagraph().run()} className={editor.isActive('paragraph') ? 'is-active' : ''}>
                    Paragraph
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}>
                    H1
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}> 
                    H2
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}> 
                    H3
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()} className={editor.isActive('heading', { level: 4 }) ? 'is-active' : ''}> 
                    H4
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()} className={editor.isActive('heading', { level: 5 }) ? 'is-active' : ''}> 
                    H5
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()} className={editor.isActive('heading', { level: 6 }) ? 'is-active' : ''}> 
                    H6
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active' : ''}> 
                    Bullet list
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'is-active' : ''}> 
                    Ordered list
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleCodeBlock().run()} className={editor.isActive('codeBlock') ? 'is-active' : ''}> 
                    Code block
                </button>
                <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={editor.isActive('blockquote') ? 'is-active' : ''}> 
                    Blockquote
                </button>
                <button type="button" onClick={() => editor.chain().focus().setHorizontalRule().run()}> 
                    Horizontal rule
                </button>
                <button type="button" onClick={() => editor.chain().focus().setHardBreak().run()}> 
                    Hard break
                </button>
                <button type="button" onClick={()=>setOpenModal(true)} className={editor.isActive('image') ? 'is-active' : ''}>
                    Image
                </button>
                <button type="button" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().chain().focus().undo().run()} >
                    Undo
                </button>
                <button type="button" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().chain().focus().redo().run()} >
                    Redo
                </button>
            </div>

            {/* Contenu de l'éditeur */}
            <EditorContent editor={editor} className="editor border p-2" />


            {/* Image selector */}
            <Modal open={openModal} onClose={()=>setOpenModal(false)} center classNames={{modal: 'rounded-3 shadow-lg bg-black border',}} closeIcon={<i className="fa fa-times text-white"></i>}>
                <div className='bg-black p-2 text-white rounded-3 shadow d-flex justify-content-start flex-wrap g-1 mt-4 overflow-y-scroll' style={{maxHeight:"70vh"}}>
                {
                    status === "pending" ? (
                        <div className=' d-flex justify-content-center align-items-center h-100 w-100'>
                            <p>Chargement des médias...</p>
                        </div>
                    ) : status === "error" ? (
                        <div className=' d-flex justify-content-center align-items-center h-100 w-100'>
                            <p>Une erreur est survenue.</p>
                        </div>
                    )
                    :dataImage?.pages?.flatMap((page, pageIndex) => 
                        page?.data?.map((media, index) => {
                            const isLastItem = pageIndex === dataImage?.pages?.length - 1 && index === page?.data?.length - 1;
                            return (
                                <img
                                    key={media.id}
                                    ref={isLastItem ? lastMediaRef : null}
                                    src={media.filePath}
                                    role="button"
                                    alt=''
                                    className='img-fluid object-fit-contain m-1 border bordercolor-primary'
                                    style={{ height: "5rem", width: "auto" }}
                                    onClick={() => { addImage(media.filePath); setOpenModal(false); }}
                                />
                            )
                        })
                    )
                }
                {isFetchingNextPage && <div className=' d-flex justify-content-center align-items-center h-100 w-100'><p className="fs-9s">Chargement...</p></div>}
                {(dataImage?.pages?.length < 0) && <IsEmpty icon={'fa-light fa-image'} message={'Aucune image'} />}
                </div>
            </Modal>
        </div>
    );
};

export default Editor